import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Tariff } from '../types/finance';
import { getTariffs, createTariff, updateTariff, type TariffsQuery, type TariffCreateDto, type TariffPatchDto } from '../lib/api/endpoints';
import { tariffFromDto } from '../lib/api/mappers';

/**
 * FE-32 — la lista de precios.
 *
 * Con `validAt` el servidor devuelve solo las vigentes ESE día (`validTo`
 * inclusivo): es la consulta del formulario de ingreso, que la repite cuando
 * cambia la fecha del servicio. Sin filtro, Administración ve todas las
 * vigencias, incluidas las cerradas, porque la historia de precios es la que
 * explica lo que se cobró.
 *
 * `crear` es también "subir precios": la vigencia nueva es otra fila. `cerrar`
 * pone `validTo` a la anterior. Los precios de una tarifa no se editan.
 */
export function useTariffs(query: TariffsQuery = {}) {
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    let cancelled = false;
    const parsed = JSON.parse(queryKey) as TariffsQuery;
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await getTariffs(parsed);
        if (cancelled) return;
        setTariffs(res.items.map(tariffFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setTariffs([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando las tarifas'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [queryKey, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  const upsert = useCallback((t: Tariff) => {
    setTariffs(prev => {
      const idx = prev.findIndex(x => x.id === t.id);
      if (idx === -1) return [...prev, t];
      const next = [...prev];
      next[idx] = t;
      return next;
    });
  }, []);

  const acciones = useMemo(() => ({
    crear: async (data: TariffCreateDto): Promise<Tariff> => {
      const t = tariffFromDto(await createTariff(data));
      upsert(t);
      return t;
    },
    actualizar: async (tariffId: string, data: TariffPatchDto): Promise<Tariff> => {
      const t = tariffFromDto(await updateTariff(tariffId, data));
      upsert(t);
      return t;
    },
  }), [upsert]);

  return { tariffs, isLoading, error, reload, ...acciones };
}
